import React, { useState } from "react";
import { RightPanel } from "../../../components/RightPanel";
import { Box, Chip, Stack, Tooltip } from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { isOfficeFile } from "../utils";

export interface IExcelDocsPreviewProps {
  open: boolean;
  path: string[];
  onClose: () => void;
}

export const ExcelDocsPreview: React.FC<IExcelDocsPreviewProps> = ({
  open,
  path,
  onClose,
}) => {
  const [selectedFileIndex, setSelectedFileIndex] = useState(0);

  const getPreviewUrl = (filePath: string): string => {
    const baseUrl = `${process.env["REACT_APP_SERVER_URL"]}/general/get-signed-url?key=${filePath}`;
    
    if (isOfficeFile(filePath)) {
      return `${process.env["REACT_APP_EXCEL_VIEW_URL_PREFIX"]}${baseUrl}`;
    }
    
    return baseUrl;
  };

  const handleFileSelect = (index: number) => {
    setSelectedFileIndex(index);
  };

  const getFileName = (filePath: string) => {
    const parts = filePath.split("/");
    return parts[parts.length - 1] || `File ${selectedFileIndex + 1}`;
  };

  const truncateFileName = (fileName: string, maxLength: number = 25) => {
    if (fileName.length <= maxLength) return fileName;
    const extension = fileName.split('.').pop();
    const nameWithoutExt = fileName.substring(0, fileName.lastIndexOf('.'));
    const truncatedName = nameWithoutExt.substring(0, maxLength - extension!.length - 4);
    return `${truncatedName}...${extension}`;
  };

  const isSingleFile = path.length === 1;

  return (
    <>
      <RightPanel
        open={open}
        onClose={onClose}
        heading={path.length > 1 ? `Preview (${path.length} files)` : "File Preview"}
        width="90%"
        version="v2"
      >
        <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
    
          {!isSingleFile && (
            <Stack
              direction="row"
              spacing={1}
              sx={{
                overflowX: "auto",
                pb: 2,
                pt: 1,
                "&::-webkit-scrollbar": {
                  height: 6,
                },
                "&::-webkit-scrollbar-thumb": {
                  backgroundColor: "rgba(0,0,0,0.2)",
                  borderRadius: 3,
                },
              }}
            >
              {path.map((filePath, index) => {
                const fullFileName = getFileName(filePath);
                const displayName = truncateFileName(fullFileName);
                
                return (
                  <Tooltip key={index} title={fullFileName} arrow placement="top">
                    <Chip
                      icon={selectedFileIndex === index ? <CheckCircleIcon /> : <DescriptionIcon />}
                      label={displayName}
                      onClick={() => handleFileSelect(index)}
                      color={selectedFileIndex === index ? "primary" : "default"}
                      variant={selectedFileIndex === index ? "filled" : "outlined"}
                      sx={{
                        height: 40,
                        fontSize: "0.875rem",
                        fontWeight: selectedFileIndex === index ? 600 : 400,
                        cursor: "pointer",
                        transition: "all 0.2s",
                        "&:hover": {
                          transform: "translateY(-2px)",
                          boxShadow: 2,
                        },
                      }}
                    />
                  </Tooltip>
                );
              })}
            </Stack>
          )}

          <Box sx={{ flex: 1, overflow: "hidden", position: "relative" }}>
            {path.map((filePath, index) => (
              <Box
                key={index}
                role="tabpanel"
                hidden={selectedFileIndex !== index}
                id={`file-tabpanel-${index}`}
                aria-labelledby={`file-tab-${index}`}
                sx={{
                  height: "100%",
                  display: selectedFileIndex === index ? "block" : "none",
                }}
              >
                {selectedFileIndex === index && (
                  <iframe
                    title={`file-view-${index}`}
                    src={getPreviewUrl(filePath)}
                    style={{
                      width: "100%",
                      height: "100%",
                      minHeight: "85vh",
                      border: "none",
                    }}
                  />
                )}
              </Box>
            ))}
          </Box>
        </Box>
      </RightPanel>
    </>
  );
};
